<script setup lang="ts">
/**
 * The app's error page — what Nuxt renders when a page request fails, above all a 404 for a path
 * that matches no page (MAR-933 §15).
 *
 * Before this existed Nuxt's own English-only default page stood here, and for a signed-in user on an
 * unknown `/app/*` path it never even rendered: the server's error re-render ran without the session
 * and `auth.global.ts` looped the user through `/login` (fixed in `server/middleware/auth.ts`). This
 * page is the other half — the dead end says what happened and hands over the way out, in the same
 * voice and shape as `NotFoundState.vue`, the record-level version of the same moment.
 *
 * The way out is `/app` for a signed-in user and `/` otherwise, through `clearError` — a plain link
 * would leave Nuxt's error state standing over the next page.
 */
import type { NuxtError } from '#app'
import { FileQuestion, AlertTriangle } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'

const props = defineProps<{ error: NuxtError }>()

const { t } = useI18n()
const { user } = useAuth()

const notFound = computed(() => (props.error?.statusCode ?? props.error?.status) === 404)
const home = computed(() => (user.value ? '/app' : '/'))

useHead({ title: () => (notFound.value ? t('errorPage.notFoundTitle') : t('errorPage.errorTitle')) })

function goHome() {
  clearError({ redirect: home.value })
}
</script>

<template>
  <main
    data-testid="error-page"
    :data-status="error?.statusCode ?? error?.status"
    class="min-h-dvh flex flex-col items-center justify-center bg-background px-6 py-24 text-center"
  >
    <component :is="notFound ? FileQuestion : AlertTriangle" class="h-8 w-8 text-muted-foreground/60" aria-hidden="true" />
    <h1 class="mt-3 text-sm font-medium text-foreground">
      {{ notFound ? t('errorPage.notFoundTitle') : t('errorPage.errorTitle') }}
    </h1>
    <p class="mt-1 max-w-sm text-sm text-muted-foreground">
      {{ notFound ? t('errorPage.notFoundHint') : t('errorPage.errorHint') }}
    </p>
    <Button as="a" :href="home" variant="outline" class="mt-5" @click.prevent="goHome">
      {{ t('errorPage.backHome') }}
    </Button>
  </main>
</template>
